import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { debounce } from 'lodash-es';
import { filter } from 'rxjs';

import { ListItem, ListProperty } from '@site-mate/dashpivot-shared-library';

import { DatePickerComponent } from 'app/shared/component/date-picker/date-picker.component';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { UserService } from 'app/user/user.service';

import { IListPropertyCell } from '../list-cell.interface';
import { ListPropertyCellService } from '../list-property-cell.service';

@Component({
  templateUrl: './list-field-date.component.html',
  styleUrls: ['./list-field-date.component.scss'],
  selector: 'cc-list-field-date',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListFieldDateComponent implements IListPropertyCell {
  @Input() item: ListItem;
  @Input() property: ListProperty;
  @Output() readonly loadPlaceholder = new EventEmitter<void>();
  @Output() readonly propertyUpdated = new EventEmitter<Record<string, unknown>>();
  debouncedPropertyUpdated: (value: NgbDateStruct) => void;
  name: string;
  timezone: string;
  @ViewChild(DatePickerComponent, { static: false }) datePicker: DatePickerComponent;
  private debounceTime = 250;
  private destroyRef = inject(DestroyRef);
  constructor(
    private readonly userService: UserService,
    private readonly listPropertyCellService: ListPropertyCellService,
  ) {
    this.debouncedPropertyUpdated = debounce(this.onPropertyUpdated, this.debounceTime);
  }

  ngOnInit() {
    this.name = `${this.property._id}-date-input`;
    this.timezone = this.userService.getTimezone();
    this.monitorExternalClicks();
  }

  private monitorExternalClicks() {
    const cellId = `${this.property._id}/${this.item._id}`;
    this.listPropertyCellService.setActiveCell(cellId);
    this.listPropertyCellService.$activeCell
      .pipe(
        filter((id) => id !== cellId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadPlaceholder.emit());
  }

  ngAfterViewInit() {
    this.datePicker.open();
  }

  onPropertyUpdated(dateValue: NgbDateStruct) {
    const dateToUpdate = dateValue || ({} as NgbDateStruct);
    this.property._dateValue = dateToUpdate;
    this.property.value = ListsHandler.getTimezoneAwareProperty(dateToUpdate, this.timezone);

    this.propertyUpdated.emit({ value: this.property.value || '' });
  }

  @HostListener('document:click', ['$event'])
  onFieldClick(event: MouseEvent) {
    const isDatePicker = (event.target as HTMLElement).closest('cc-date-picker, ngb-datepicker') !== null;

    if (!isDatePicker) {
      this.loadPlaceholder.emit();
    }
  }
}
